/**
 * voice-intake.ts
 * locx-voz-multi-intencion — API client for the "Dictar" feature (Phase 9,
 * apps/operator UI). Deliberately its OWN file rather than growing `api.ts`
 * (task 9.6) — self-contained per-feature grouping, same reasoning `api.ts`'s
 * own header gives for staying a "deliberately small subset" rather than one
 * ever-growing file.
 *
 * Reuses the SAME `request<T>`/`ApiError`/`API_BASE` machinery as `api.ts` —
 * not reimplemented here. `uploadFile()` (existing, `api.ts`) is reused
 * unchanged for the audio blob — same kernel `POST /files` the fuel-load
 * signature already uses (design.md D2).
 *
 * locx-voz-servicio-cross-tenant PR2 (2026-09-15): `servicio`/`servicio_futuro`
 * are now reachable confirm branches too — `VoiceIntakeConfirm`'s
 * discriminated union covers all 4 intents. The 3 functions below back the
 * confirm screen's contractor/service-type pickers and the new "Mis
 * solicitudes enviadas" list, consuming the 3 GETs PR1 already shipped.
 *
 * operator-mis-pedidos-repuestos (2026-09-19): `getMyPurchaseCases` backs
 * "Mis pedidos de repuestos" — the `repuesto` intent's own equivalent of "Mis
 * solicitudes enviadas" above. Hits Compras' own `GET /procurement/my-cases`
 * (`packages/modules/compras/src/http/compras.routes.ts:750`, guard
 * `requi.solicitud.leer-propias`, migration 0200/AZ-21) — NOT a servicios
 * route, same cross-module-HTTP-call posture as the two Compras pickers
 * below (this app calling two different backend modules over plain HTTP is
 * not a module-boundary violation; that invariant is backend-internal).
 *
 * The two Compras pickers below (`searchCatalogItems`, `listDeliveryLocations`)
 * hit `compras`' own unguarded `/compras/requisition-options/*` routes
 * (any authenticated tenant user may read them — same picker used by the
 * "Solicitar compra" case-creation form) — this is a plain cross-app HTTP
 * call, not a module-boundary violation (that invariant is backend-internal,
 * `packages/modules/servicios` never imports `packages/modules/compras`;
 * the operator SPA calling two different backend routes is unrelated). Cost
 * centers reuse the SAME `/compras/requisition-options/cost-centers` catalog
 * — REQUI's own, per design.md D6, not `api.ts`'s existing
 * `/servicios/order-options/cost-centers` (a different catalog, used by fuel
 * loads).
 */
import type {
  VoiceIntakeIngest,
  VoiceIntakeResponse,
  VoiceIntakeConfirm,
  VoiceIntakeConfirmResponse,
  VoiceIntakeDiscardResponse,
  PartnerContractorsResponse,
  PartnerServiceTypesResponse,
  MySentServiceOrderRequestsResponse,
} from '@suite/contracts-servicios';
import type { MyCasesResponse, CaseStatus } from '@suite/contracts-compras';
import type { PaginationQuery } from '@suite/contracts-kernel';
import { fetchFileBlob, request } from './http.js';
import { uploadFile } from './api.js';

// ---------------------------------------------------------------------------
// The 4 (+1 discard) voice-intake endpoints — design.md D3
// ---------------------------------------------------------------------------

/** POST /servicios/voice-intakes — capture (no model call, D3). Fast. */
export function ingestVoiceIntake(body: VoiceIntakeIngest): Promise<VoiceIntakeResponse> {
  return request<VoiceIntakeResponse>('/servicios/voice-intakes', { method: 'POST', body });
}

/**
 * POST /servicios/voice-intakes/:id/process — transcribe→classify→resolve.
 * ALWAYS 200 (D13/D3) — never throws for a provider problem, only for a
 * genuine network/404/500 failure (see ApiError).
 */
export function processVoiceIntake(id: string): Promise<VoiceIntakeResponse> {
  return request<VoiceIntakeResponse>(`/servicios/voice-intakes/${id}/process`, { method: 'POST' });
}

export interface DictationSubmission {
  readonly audio: Blob;
  readonly clientIntakeId: string;
  readonly durationMs: number;
  readonly assetId?: string;
}

/**
 * Upload the audio, ingest it and process it: everything between "stopped
 * recording" and "ready to confirm". Shared by the live recording screen and
 * the offline queue (dictation-queue.ts). Safe to repeat: a replayed ingest
 * with the same clientIntakeId returns the existing intake.
 */
export async function submitDictation(dictation: DictationSubmission): Promise<VoiceIntakeResponse> {
  const { fileId } = await uploadFile(dictation.audio, `voice-intake-${dictation.clientIntakeId}.webm`);
  const ingested = await ingestVoiceIntake({
    audioFileId: fileId,
    clientIntakeId: dictation.clientIntakeId,
    durationMs: dictation.durationMs,
    ...(dictation.assetId ? { assetId: dictation.assetId } : {}),
  });
  return processVoiceIntake(ingested.id);
}

/** GET /servicios/voice-intakes/:id — reload/back-button safety (D3). */
export function getVoiceIntake(id: string): Promise<VoiceIntakeResponse> {
  return request<VoiceIntakeResponse>(`/servicios/voice-intakes/${id}`);
}

/** POST /servicios/voice-intakes/:id/confirm — the only path that writes a domain row (D9). */
export function confirmVoiceIntake(id: string, body: VoiceIntakeConfirm): Promise<VoiceIntakeConfirmResponse> {
  return request<VoiceIntakeConfirmResponse>(`/servicios/voice-intakes/${id}/confirm`, { method: 'POST', body });
}

/** POST /servicios/voice-intakes/:id/discard — "Descartar" (design.md §3 item 5). */
export function discardVoiceIntake(id: string): Promise<VoiceIntakeDiscardResponse> {
  return request<VoiceIntakeDiscardResponse>(`/servicios/voice-intakes/${id}/discard`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Servicio/servicio_futuro cross-tenant pickers (locx-voz-servicio-cross-tenant
// PR2, design.md D1/D5/D6) — "which contractor?", the chosen contractor's own
// service-type catalog, and "my sent requests". Mirrors the 3 GETs
// `service-order-request.routes.ts` shipped in PR1.
// ---------------------------------------------------------------------------

/** GET /servicios/partner/contractors — every ACTIVE locx.service_order counterparty. Empty → the confirm screen hides servicio/servicio_futuro (D1). */
export function getPartnerContractors(): Promise<PartnerContractorsResponse> {
  return request<PartnerContractorsResponse>('/servicios/partner/contractors');
}

/** GET /servicios/partner/service-types?ownerCompanyId= — the chosen contractor's OWN service-type catalog. 404s without an active partnership. */
export function getPartnerServiceTypes(ownerCompanyId: string): Promise<PartnerServiceTypesResponse> {
  return request<PartnerServiceTypesResponse>(`/servicios/partner/service-types?ownerCompanyId=${encodeURIComponent(ownerCompanyId)}`);
}

/** GET /servicios/partner/service-order-requests — "Mis solicitudes enviadas" (D5), a counterparty read of what I sent. */
export function getMySentServiceOrderRequests(pagination: Partial<PaginationQuery> = {}): Promise<MySentServiceOrderRequestsResponse> {
  const params = new URLSearchParams();
  if (pagination.page !== undefined) params.set('page', String(pagination.page));
  if (pagination.pageSize !== undefined) params.set('pageSize', String(pagination.pageSize));
  const qs = params.toString();
  return request<MySentServiceOrderRequestsResponse>(`/servicios/partner/service-order-requests${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// "Mis pedidos de repuestos" (operator-mis-pedidos-repuestos, AZ-21) —
// requesterId is ALWAYS TenantContext.current().userId server-side (never a
// param this client could send), same ownership contract as
// getMySentServiceOrderRequests above.
// ---------------------------------------------------------------------------

export interface MyPurchaseCasesFilters extends Partial<PaginationQuery> {
  status?: CaseStatus;
}

/** GET /procurement/my-cases — "Mis pedidos de repuestos" (the repuesto intent's own read-back, D5's counterpart for purchase_case). */
export function getMyPurchaseCases(filters: MyPurchaseCasesFilters = {}): Promise<MyCasesResponse> {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  if (filters.status !== undefined) params.set('status', filters.status);
  const qs = params.toString();
  return request<MyCasesResponse>(`/procurement/my-cases${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// Repuesto field pickers (Compras' own catalogs, D6) — see header comment.
// ---------------------------------------------------------------------------

export interface CatalogItemOption {
  id: string;
  code: string | null;
  description: string;
  unit: string;
}

export interface RequisitionOptionPickerItem {
  id: string;
  name: string;
}

/** GET /compras/requisition-options/catalog-items?q= — real search, <2 chars returns empty (buscador, not a listing). */
export function searchCatalogItems(q: string): Promise<{ items: CatalogItemOption[] }> {
  return request<{ items: CatalogItemOption[] }>(`/compras/requisition-options/catalog-items?q=${encodeURIComponent(q)}`);
}

/** GET /compras/requisition-options/cost-centers — REQUI's own catalog (design.md D6), unguarded. */
export function listRequisitionCostCenters(): Promise<{ items: RequisitionOptionPickerItem[] }> {
  return request<{ items: RequisitionOptionPickerItem[] }>('/compras/requisition-options/cost-centers');
}

/** GET /compras/requisition-options/delivery-locations — unguarded. */
export function listDeliveryLocations(): Promise<{ items: RequisitionOptionPickerItem[] }> {
  return request<{ items: RequisitionOptionPickerItem[] }>('/compras/requisition-options/delivery-locations');
}

// ---------------------------------------------------------------------------
// Playback (design.md §3 item 1 — GET /files/:id, requires the session cookie)
// ---------------------------------------------------------------------------

/**
 * Resolves the recording's playback URL as a same-origin blob: URL. NOT a
 * plain `<audio :src>` pointing at the kernel route directly — `GET
 * /files/:id` is tenant-scoped (auth middleware reads the session cookie),
 * and a cross-origin `<audio>` element never sends credentials by default,
 * so the request would 401/404. Same raw-`fetch` + credentials:'include'
 * posture as `api.ts`'s `downloadEmploymentCertificatePdf`. Caller MUST
 * `URL.revokeObjectURL(...)` when done (e.g. `onBeforeUnmount`).
 */
export async function getAudioPlaybackUrl(fileId: string): Promise<string> {
  const blob = await fetchFileBlob(fileId, 'No se pudo cargar el audio.');
  return URL.createObjectURL(blob);
}
